const WORKER_CODE = `
      self.onmessage = function(e) {
      const { buffer, bins, minProvided, width, height, maxProvided, minVal, maxVal, crop } = e.data;

      const pixels = new Uint16Array(buffer);
      const hist = new Uint32Array(bins);

      let min = minVal;
      let max = maxVal;
      if (!minProvided || !maxProvided) {
        min = 65535;
        max = 0;
        for (let i = 0; i < pixels.length; i++) {
        const v = pixels[i];
        if (v < min) min = v;
        if (v > max) max = v;
        }
      }

      if (min === max) {
        min = Math.max(0, min - 1);
        max = Math.min(65535, max + 1);
      }

      const range = max - min || 1;
      const [x, y, w, h] = crop ?? [0, 0, width, height]

      for (let k = y; k < y + h; k++) {
        for (let j = x; j < x + w; j++) {
          const i = j + k * width;
          const v = pixels[i];
          let idx = Math.floor(((v - min) / range) * (bins - 1));
          if (idx < 0) idx = 0;
            else if (idx >= bins) idx = bins - 1;
            hist[idx]++;
        }
      }

      // Transfer the histogram buffer back
      self.postMessage({ hist: hist.buffer }, [hist.buffer]);
      };
    `;

type CancelableHistogramPromise = Promise<Uint32Array> & { cancel: () => void };

export default function computeHistogramInWorker(
  pixelBuffer: Uint16Array,
  binsCount: number,
  min?: number,
  max?: number,
  width?: number,
  height?: number,
  crop?: [number, number, number, number]
): CancelableHistogramPromise {
  let cancel: () => void;

  const promise: CancelableHistogramPromise = new Promise<Uint32Array>((resolve, reject) => {
    const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);

    // Send data to worker. Transfer the pixel buffer for zero-copy.
    worker.postMessage(
      {
        buffer: pixelBuffer.buffer,
        bins: binsCount,
        minProvided: min !== undefined,
        maxProvided: max !== undefined,
        minVal: min ?? 0,
        maxVal: max ?? 65535,
        crop: crop,
        width,
        height,
      },
      []
    );

    const cleanup = () => {
      promise.cancel = () => {};
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
      URL.revokeObjectURL(workerUrl);
    };

    const onError = (err: ErrorEvent) => {
      cleanup();
      reject(err);
    };

    // When worker responds, draw the histogram to the canvas (duplicate of the drawing logic below)
    const onMessage = (ev: MessageEvent) => {
      resolve(new Uint32Array(ev.data.hist));

      cleanup();
    };
    cancel = () => {
      cleanup();
      reject(new Error('Histogram computation cancelled'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
  }) as CancelableHistogramPromise;

  promise.cancel = cancel;

  return promise;
}
